<template>
  <v-container class="pt-3 pb-6 px-6">

    <!-- Floating Button -->
    <floating-button @click="save()" save :disabled="invalid" v-if="authUser"/>

    <!-- Page Header -->
    <page-header
      :title="isNew + ' Parque Eólico'"
      icon="mdi-map-marker"
      divider="-"
      :breadcrumbs="breadcrumbs.items"
      refresh
      v-on:refresh="loadFarms()"
    ></page-header>

    <v-row align="center" justify="space-around">
      <v-col cols="12">
        <v-card>
          <!-- Loading Bar -->
          <v-progress-linear
            indeterminate
            v-if="isLoadingData"
          ></v-progress-linear>
          <!-- Content -->
          <v-card-text v-if="authUser">
            <v-container>
              <v-row align="center" justify="space-around">
                <!-- Wind Farm Name -->
                <v-col cols="4">
                  <v-text-field
                    v-model="wind_farm.name"
                    :error-messages="nameErrors"
                    label="Nombre del parque"
                    outlined
                    counter="100"
                    maxlength="100"
                    @blur="$v.wind_farm.name.$touch()"
                  ></v-text-field>
                </v-col>
                <!-- Wind Farm Owner -->
                <v-col cols="4">
                  <v-text-field
                    v-model="wind_farm.owner"
                    :error-messages="ownerErrors"
                    label="Propiedad del parque"
                    outlined
                    counter="100"
                    maxlength="100"
                    @blur="$v.wind_farm.owner.$touch()"
                  ></v-text-field>
                </v-col>
                <!-- Substation Coordiantes -->
                <v-col cols="4">
                  <v-text-field
                    v-model="wind_farm.substation_coordinates"
                    label="Ubicación de la subestación"
                    placeholder="Lat: -3.0003212122 Long: 300.23232342"
                    hint="Introducir coordenadas"
                    outlined
                    counter="100"
                    maxlength="100"
                  ></v-text-field>
                </v-col>
                <!-- Supervisor Name -->
                <v-col cols="4">
                  <v-text-field
                    v-model="wind_farm.supervisor_full_name"
                    label="Nombre del supervisor"
                    outlined
                    counter="100"
                    maxlength="100"
                  ></v-text-field>
                </v-col>
                <!-- Contact Phone Number -->
                <v-col cols="4">
                  <v-text-field
                    v-model="wind_farm.supervisor_phone_number"
                    label="Teléfono de contacto"
                    outlined
                    counter="20"
                    maxlength="20"
                  ></v-text-field>
                </v-col>
                <!-- Email -->
                <v-col cols="4">
                  <v-text-field
                    v-model="wind_farm.supervisor_email"
                    :error-messages="supervisorEmailErrors"
                    label="Email"
                    outlined
                    counter="100"
                    maxlength="100"
                    @blur="$v.wind_farm.supervisor_email.$touch()"
                  ></v-text-field>
                </v-col>
                <!-- Turbine Type -->
                <v-col cols="4">
                  <v-autocomplete
                    v-model="wind_farm.wind_turbine_types"
                    :error-messages="turbineTypeErrors"
                    :items="turbineList"
                    label="Fabricante AG"
                    :item-text="text"
                    item-value="id"
                    chips
                    deletable-chips
                    outlined
                    multiple
                    @blur="$v.wind_farm.wind_turbine_types.$touch()"
                  ></v-autocomplete>
                </v-col>
                <!-- Turbine Blade Type -->
                <v-col cols="4">
                  <v-autocomplete
                    v-model="wind_farm.wind_turbine_blade_types"
                    :error-messages="bladeType"
                    :items="bladeList"
                    label="Fabricante Pala"
                    item-value="id"
                    :item-text="text"
                    chips
                    deletable-chips
                    outlined
                    multiple
                    @blur="$v.wind_farm.wind_turbine_blade_types.$touch()"
                  ></v-autocomplete>
                </v-col>
                <!-- Turbines Number -->
                <v-col cols="2">
                  <v-text-field
                    v-model="wind_farm.number_of_wind_turbines"
                    :error-messages="numberOfWindTurbinesErrors"
                    label="Número de AG"
                    type="number"
                    outlined
                    min="1"
                    @blur="$v.wind_farm.number_of_wind_turbines.$touch()"
                  ></v-text-field>
                </v-col>
                <!-- State -->
                <v-col cols="2">
                  <v-select
                    v-model="wind_farm.is_active"
                    :error-messages="stateErrors"
                    :items="state"
                    label="Estado"
                    outlined
                    @blur="$v.wind_farm.is_active.$touch()"
                  >
                    <template v-slot:selection="data">
                      <v-chip
                        style="cursor: pointer"
                        :color="data.item.value ? 'primary' : 'deep-orange'"
                        :class="{'white--text': !data.item.value}"
                      >{{ data.item.text }}</v-chip>
                    </template>
                  </v-select>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import FloatingButton from "@/components/FloatingButton";
import PageHeader from "@/components/PageHeader";
import { validationMixin } from 'vuelidate';
import { required, email, minValue } from 'vuelidate/lib/validators';
import { WindFarmRepository } from "@/api/Repositories";
import Util from "@/util/index";

export default {
  components: {
    FloatingButton,
    PageHeader
  },
  mixins: [validationMixin],
  validations: {
    wind_farm: {
      name: { required },
      owner: { required },
      supervisor_email: { email },
      number_of_wind_turbines: { required, minValue: minValue(1) },
      wind_turbine_types: { required },
      wind_turbine_blade_types: { required  },
      is_active: { required }
    }
  },
  data: () => ({
    authUser: false,
    isLoadingData: false,
    oldName: '',
    wind_farm: {
      name: '',
      owner: '',
      supervisor_full_name: '',
      supervisor_phone_number: '',
      supervisor_email: '',
      substation_coordinates: '',
      number_of_wind_turbines: '',
      wind_turbine_types: [],
      wind_turbine_blade_types: [],
      is_active: ''
    },
    alert: true,
    state: [
      { text: 'Activo', value: true },
      { text: 'Inactivo', value: false }
    ],
    breadcrumbs: {
      items: [
        { text: "Configuración", disabled: true },
        { text: "Parques Eólicos", disabled: true }
      ]
    },
  }),
  computed: {
    loading: {
      get () {
        return this.$store.state.loading
      },
      set (val) {
        this.$store.commit('loading', val)
      }
    },
    isNew () {
      return this.$route.params.id ? 'Modificar' : 'Añadir'
    },
    invalid () {
      return this.$v.$invalid && this.$v.$dirty
    },
    farms () {
      return this.$store.state.farms.wind_farms
    },
    turbineList () {
      return this.$store.state.farms.turbineList
    },
    bladeList () {
      return this.$store.state.farms.bladeList
    },
    uniqueName () {
      if (this.wind_farm.name) {
        if (this.$route.params.id) {
          let nameExist = this.farms.some(element => {
            return element.name.trim() === this.wind_farm.name.trim()
            && element.name.trim() !== this.oldName.trim()
          })
          return !nameExist
        } else {
          let nameExist = this.farms.some(element => {
            return element.name.trim() === this.wind_farm.name.trim()
          })
          return !nameExist
        }
      } else {
        return true
      }
    },
    nameErrors () {
      const errors = []
      if (!this.$v.wind_farm.name.$dirty) return errors
      !this.$v.wind_farm.name.required && errors.push('Obligatorio.')
      !this.uniqueName && errors.push('Parque ya existe')
      return errors
    },
    ownerErrors () {
      const errors = []
      if (!this.$v.wind_farm.owner.$dirty) return errors
      !this.$v.wind_farm.owner.required && errors.push('Obligatorio.')
      return errors
    },
    supervisorEmailErrors () {
      const errors = []
      if (!this.$v.wind_farm.supervisor_email.$dirty) return errors
      !this.$v.wind_farm.supervisor_email.email && errors.push('Email invalido')
      return errors
    },
    numberOfWindTurbinesErrors () {
      const errors = []
      if (!this.$v.wind_farm.number_of_wind_turbines.$dirty) return errors
      !this.$v.wind_farm.number_of_wind_turbines.required && errors.push('Obligatorio.')
      !this.$v.wind_farm.number_of_wind_turbines.minValue && errors.push('Minimo 1')
      return errors
    },
    turbineTypeErrors () {
      const errors = []
      if (!this.$v.wind_farm.wind_turbine_types.$dirty) return errors
      !this.$v.wind_farm.wind_turbine_types.required && errors.push('Obligatorio.')
      return errors
    },
    bladeType () {
      const errors = []
      if (!this.$v.wind_farm.wind_turbine_blade_types.$dirty) return errors
      !this.$v.wind_farm.wind_turbine_blade_types.required && errors.push('Obligatorio.')
      return errors
    },
    stateErrors () {
      const errors = []
      if (!this.$v.wind_farm.is_active.$dirty) return errors
      !this.$v.wind_farm.is_active.required && errors.push('Obligatorio.')
      return errors
    }
  },
  methods: {
    text (item) {
      return item.manufacturer + ' - ' + item.model
    },
    loadFarms () {
      this.isLoadingData = true
      this.$store.dispatch('farms/getTurbineList')
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando la lista de turbinas."
            Util.showErrorToast(this, error, errorToastMessage);
          }
        })
      this.$store.dispatch('farms/getBladeList')
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando la lista de palas."
            Util.showErrorToast(this, error, errorToastMessage);
          }
        })
      this.$store.dispatch('farms/getFarms')
        .then(() => {
          this.getFarm()
        })
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando los parques eólicos."
            Util.showErrorToast(this, error, errorToastMessage);
            this.farmList()
          }
        })
    },
    getFarm () {
      var id = this.$route.params.id
      if (id) {
        WindFarmRepository.get(id)
          .then(response => {
            let tempFarm = response.data.data
            tempFarm.wind_turbine_types = tempFarm.wind_turbine_types.map(r => r.id);
            tempFarm.wind_turbine_blade_types = tempFarm.wind_turbine_blade_types.map(r => r.id);

            this.wind_farm = tempFarm
            this.oldName = tempFarm.name
          })
          .catch(error => {
            if (error.response.status === 403 ) {
              this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
                color: "error",
                x: "center",
                y: "top",
                icon: "mdi-information",
                timeout: 0,
                showClose: true,
                closeText: "Cerrar"
              });
              this.$router.push({ name: 'home'})
            } else {
              var errorToastMessage = "Ha ocurrido un error cargando el parque eólico."
              Util.showErrorToast(this, error, errorToastMessage);
            }
            this.farmList();
          })
      } else {
        this.wind_farm = {
          name: '',
          owner: '',
          supervisor_full_name: '',
          supervisor_phone_number: '',
          supervisor_email: '',
          substation_coordinates: '',
          number_of_wind_turbines: '',
          wind_turbine_types: [],
          wind_turbine_blade_types: [],
          is_active: ''
        }
      }
      this.$v.$reset()
      this.authUser = true
      this.isLoadingData = false
    },
    farmList () {
      this.$router.push({
        name: "farm-list"
      });
    },
    save () {
      this.$route.params.id ? this.editFarm() : this.createFarm()
    },
    createFarm () {
      this.$v.$touch()
      if (!this.$v.$invalid && this.uniqueName) {
        this.loading = true
        var int = parseInt(this.wind_farm.number_of_wind_turbines)
        this.wind_farm.number_of_wind_turbines = int

        this.$store.dispatch('farms/createFarm', this.wind_farm)
          .then(() => {
            this.$toast("Parque eólico creado correctamente.", {
              color: "success",
              x: "center",
              y: "top",
              icon: "mdi-information"
            });
            this.farmList()
          })
          .catch(error => {
            var errorToastMessage = "Ha ocurrido un error creando el parque eólico."
            Util.showErrorToast(this, error, errorToastMessage);
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    editFarm () {
      this.$v.$touch()
      if (!this.$v.$invalid && this.uniqueName) {
        this.loading = true
        var int = parseInt(this.wind_farm.number_of_wind_turbines)
        this.wind_farm.number_of_wind_turbines = int

        this.$store.dispatch('farms/editFarm', this.wind_farm)
          .then(() => {
            this.$toast("Parque eólico modificado correctamente.", {
              color: "success",
              x: "center",
              y: "top",
              icon: "mdi-information"
            });
            this.farmList()
          })
          .catch(error => {
            var errorToastMessage = "Ha ocurrido un error modificando el parque eólico."
            Util.showErrorToast(this, error, errorToastMessage);
          })
          .finally(() => {
            this.loading = false
          })
      }
    }
  },
  created () {
    this.loadFarms()
  }
}
</script>
